import type { Foyer, Personne } from './types.js';

export const TAILLE_CIBLE = 4;
export const TAILLE_MIN = 3;
export const TAILLE_MAX = 5;
/** Valeurs admises pour « convives par table » (réglage de l'événement). */
export const TAILLE_CIBLE_MIN = 3;
export const TAILLE_CIBLE_MAX = 10;

/** Réglage de taille : soit une cible (plage cible ± 1), soit une plage explicite min–max (paramétrage de l'événement). */
export type Cible = number | { min: number; max: number };

/** Plage conseillée : min–max du réglage, ou cible ± 1 (règle 7, généralisée). */
export function plageDe(cible: Cible = TAILLE_CIBLE): { min: number; max: number } {
  if (typeof cible === 'number') return { min: Math.max(2, cible - 1), max: cible + 1 };
  return { min: Math.max(2, Math.min(cible.min, cible.max)), max: Math.max(cible.min, cible.max) };
}

/** Taille visée (milieu de la plage) utilisée pour le nombre d'hôtes idéal. */
export function cibleDe(cible: Cible = TAILLE_CIBLE): number {
  if (typeof cible === 'number') return cible;
  return Math.round((cible.min + cible.max) / 2);
}

/** Règle 2 : seuls les présents comptent. */
export function membresPresents(foyer: Foyer): Personne[] {
  return foyer.membres.filter((m) => m.participe);
}

export function taillePresente(foyer: Foyer): number {
  return membresPresents(foyer).length;
}

/** Foyers avec au moins une personne présente. */
export function foyersPresents(foyers: Foyer[]): Foyer[] {
  return foyers.filter((f) => taillePresente(f) > 0);
}

export function nombreParticipants(foyers: Foyer[]): number {
  return foyers.reduce((n, f) => n + taillePresente(f), 0);
}

/** Règle 5 : nombre idéal d'hôtes par étape ≈ participants ÷ taille cible (4 par défaut). */
export function nombreHotesIdeal(participants: number, cible: Cible = TAILLE_CIBLE): number {
  return Math.max(1, Math.round(participants / cibleDe(cible)));
}

export function indexerFoyers(foyers: Foyer[]): Map<string, Foyer> {
  return new Map(foyers.map((f) => [f.id, f]));
}

/** Volontaires présents (foyers qui acceptent de recevoir). */
export function volontaires(foyers: Foyer[]): Foyer[] {
  return foyersPresents(foyers).filter((f) => f.veutRecevoir);
}

/**
 * Plus grande table possible chez un volontaire présent, hôtes compris.
 * null = aucune borne (pas de volontaire, ou au moins un volontaire sans limite déclarée).
 */
export function capacitePossible(foyers: Foyer[]): number | null {
  const v = volontaires(foyers);
  if (v.length === 0 || v.some((f) => f.capaciteMax === undefined)) return null;
  return Math.max(...v.map((f) => f.capaciteMax as number));
}

export interface BornesTailles {
  /** Le minimum réglé ne peut pas dépasser la plus petite capacité déclarée (sinon cet hôte ne serait jamais dans la plage). */
  minMax: number | null;
  /** Le maximum réglé ne peut pas dépasser la capacité que les hôtes nécessaires d'une étape atteignent tous. */
  maxMax: number | null;
  /** Nombre d'hôtes par étape retenu pour calculer `maxMax`. */
  hotesParEtape: number;
}

/**
 * Bornes cohérentes pour la plage « convives par table » : avec des capacités de 6, 6, 8×8 et 10 pour
 * 3 hôtes par étape, le maximum vaut 8 (la 3e plus grande capacité) et le minimum au plus 6.
 * null = pas de borne (aucun volontaire, ou un volontaire sans limite déclarée).
 */
export function bornesTailles(foyers: Foyer[], participants: number, cible: Cible = TAILLE_CIBLE): BornesTailles {
  const v = volontaires(foyers);
  const hotesParEtape = Math.max(1, Math.round(participants / cibleDe(cible)));
  if (v.length === 0 || v.some((f) => f.capaciteMax === undefined)) return { minMax: null, maxMax: null, hotesParEtape };
  const caps = v.map((f) => f.capaciteMax as number).sort((a, b) => b - a);
  const k = Math.min(hotesParEtape, caps.length);
  return { minMax: Math.min(...caps), maxMax: caps[k - 1] as number, hotesParEtape };
}

export interface PrevisionTables {
  /** Taille prévue de chaque table (hôte compris), dans l'ordre des hôtes. */
  tailles: number[];
  min: number;
  max: number;
  /** Personnes qu'aucune table ne peut accueillir dans les capacités déclarées. */
  manque: number;
}

/**
 * Prévision réaliste des tables d'une étape : les participants sont répartis entre les hôtes
 * en équilibrant, sans dépasser la capacité déclarée de chacun (comme le fait le moteur).
 */
export function previsionTables(participants: number, hotes: Foyer[]): PrevisionTables {
  if (hotes.length === 0) return { tailles: [], min: 0, max: 0, manque: participants };
  const caps = hotes.map((h) => h.capaciteMax ?? Number.POSITIVE_INFINITY);
  const tailles = hotes.map((h) => taillePresente(h));
  let restant = participants - tailles.reduce((a, b) => a + b, 0);
  let manque = 0;
  while (restant > 0) {
    let k = -1;
    for (let i = 0; i < tailles.length; i++) {
      if ((tailles[i] as number) < (caps[i] as number) && (k === -1 || (tailles[i] as number) < (tailles[k] as number))) k = i;
    }
    if (k === -1) {
      manque = restant; // plus aucune place : le moteur devra déborder
      let m = 0;
      for (let i = 1; i < tailles.length; i++) if ((tailles[i] as number) < (tailles[m] as number)) m = i;
      tailles[m] = (tailles[m] as number) + restant;
      restant = 0;
      break;
    }
    tailles[k] = (tailles[k] as number) + 1;
    restant--;
  }
  return { tailles, min: Math.min(...tailles), max: Math.max(...tailles), manque };
}

/**
 * Nombre d'hôtes à trouver par étape : l'idéal participants ÷ cible, relevé si nécessaire
 * pour que les capacités déclarées des volontaires couvrent tout le monde.
 */
export function nombreHotesNecessaire(participants: number, cible: Cible, candidats: Foyer[]): number {
  const ideal = nombreHotesIdeal(participants, cible);
  const caps = candidats.map((f) => f.capaciteMax ?? Number.POSITIVE_INFINITY).sort((a, b) => b - a);
  let somme = 0;
  let k = 0;
  for (const c of caps) {
    k++;
    somme += c;
    if (somme >= participants) break;
  }
  if (somme < participants) k = Math.max(k, caps.length);
  return Math.max(ideal, k, 1);
}
